import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import SocketService from "../services/SocketService";
import FindingLawyer from "../components/call/FindingLawyer";
import CallScreen from "../components/call/CallScreen";
import lawyerImg from "../assets/images/hireNow.png";

const STRIPE_JS = "https://js.stripe.com/v3/";

// The OTP-verified auth token is persisted here so it survives the checkout
// reset after payment and is still available for the endCall API. It is removed
// once the call ends.
const AUTH_TOKEN_KEY = "cc_auth_token";

// Load the Stripe.js script once and resolve when window.Stripe is available.
const loadStripeJs = () =>
  new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    let script = document.querySelector(`script[src="${STRIPE_JS}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = STRIPE_JS;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.Stripe));
    script.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
  });

const HOW_IT_WORKS = [
  { icon: "bi-clipboard-check", title: "Provide Details", text: "Select category, jurisdiction, describe your issue and choose duration." },
  { icon: "bi-person-vcard", title: "We Find a Lawyer", text: "We match you with the most suitable and available lawyer." },
  { icon: "bi-camera-video", title: "Connect Instantly", text: "Get connected via secure video call in under 60 seconds." },
  { icon: "bi-chat-dots", title: "Get Legal Help", text: "Discuss your case and get expert legal advice." },
];

const ConsultationCall = () => {
  // Form state
  const [categoriesList, setCategoriesList] = useState([]);
  const [jurisdictionsList, setJurisdictionsList] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [jurisdictionId, setJurisdictionId] = useState("");
  const [caseDescription, setCaseDescription] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);

  // Call flow state: idle | searching | connected
  const [callPhase, setCallPhase] = useState("idle");
  const [matchedLawyers, setMatchedLawyers] = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [lawyersFound, setLawyersFound] = useState(false); // initiate API returned
  const [activeCall, setActiveCall] = useState(null); // { callId, channel, creds, lawyer }

  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const clientSecretRef = useRef(null);
  const publishableKeyRef = useRef(null);
  const callRef = useRef(null);          // mirrors activeCall for socket handlers
  const phaseRef = useRef("idle");
  const matchedRef = useRef([]);         // mirrors matchedLawyers for socket handlers
  const searchTimer = useRef(null);
  callRef.current = activeCall;
  phaseRef.current = callPhase;

  // ---- Load dropdowns + packages -----------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const base = process.env.REACT_APP_API_URL;
        const [cats, dropdown, pkgs] = await Promise.all([
          axios.get(`${base}/getCategories`),
          axios.get(`${base}/getDropdownData`),
          axios.get(`${base}/getCallPackages`),
        ]);
        if (cats?.data?.status) setCategoriesList(cats.data.data || []);
        if (dropdown?.data?.status) setJurisdictionsList(dropdown.data.data.jurisdictions || []);
        if (pkgs?.data?.status) {
          const list = pkgs.data.data || [];
          setPackages(list);
          const def = list.find((p) => p.is_popular) || list[0];
          if (def) setSelectedPackage(def);
        }
      } catch (e) {
        console.error("Error fetching consultation data:", e);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  // ---- OTP ----------------------------------------------------------------
  const handleSendOtp = async () => {
    if (!fullName || !email) {
      toast.error("Please provide both name and email.");
      return;
    }
    setOtpLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", fullName);
      formData.append("email", email);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/resendOTP`, formData);
      if (res?.data?.status) {
        toast.success(res?.data?.message || "OTP sent successfully!");
      } else {
        toast.error(res?.data?.message || "Failed to send OTP.");
      }
    } catch (e) {
      toast.error("An error occurred while sending OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    const verifyOtp = async () => {
      if (otp.length !== 4 || !email) return;
      try {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("otp", otp);
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/verifyOTP`, formData);
        if (res?.data?.status) {
          toast.success(res?.data?.message || "OTP verified successfully!");
          setOtpVerified(true);
          setAuthToken(res.data.data.auth_token);
          localStorage.setItem(AUTH_TOKEN_KEY, res.data.data.auth_token);
        } else {
          toast.error(res?.data?.message || "Invalid OTP.");
          setOtpVerified(false);
          setAuthToken(null);
          localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      } catch (e) {
        toast.error("An error occurred during OTP verification.");
        setOtpVerified(false);
        setAuthToken(null);
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    };
    verifyOtp();
  }, [otp, email]);

  // Prefer the persisted token: after payment the checkout state (incl. the
  // authToken state) is reset, but the call flow (endCall etc.) still needs it.
  const getAuthToken = () => authToken || localStorage.getItem(AUTH_TOKEN_KEY);

  // ---- Open checkout ------------------------------------------------------
  const handleStartConnecting = () => {
    if (!categoryId || !jurisdictionId || !caseDescription.trim()) {
      toast.error("Please choose a category, jurisdiction and describe your issue.");
      return;
    }
    if (!selectedPackage) {
      toast.error("Please select a call duration.");
      return;
    }
    setShowCheckout(true);
  };

  // ---- Create payment intent (authorize-only / manual capture) ------------
  const initCard = async () => {
    if (!authToken || !selectedPackage) return;
    setPayLoading(true);
    try {
      const payData = new FormData();
      payData.append("amount", selectedPackage.price);
      payData.append("call_package_id", selectedPackage.id);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/consultationPayment`,
        payData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Failed to start payment.");
        return;
      }
      clientSecretRef.current = res.data.data.paymentIntent;
      publishableKeyRef.current = res.data.data.publishableKey;
      // Render the card box first; the actual Stripe element is mounted in the
      // effect below once #cc-card-element exists in the DOM.
      setCardReady(true);
    } catch (e) {
      toast.error("An error occurred while preparing payment.");
    } finally {
      setPayLoading(false);
    }
  };

  // Mount the Stripe card element once the card box has been rendered.
  // The effect owns the element's whole lifecycle: it mounts a fresh card when
  // cardReady flips on and destroys it on cleanup (close/reopen or StrictMode's
  // mount→cleanup→mount). We intentionally do NOT guard on cardRef.current — a
  // stale ref surviving a previous cycle must not block a re-mount, otherwise
  // reopening the modal leaves #cc-card-element empty.
  useEffect(() => {
    if (!cardReady || !publishableKeyRef.current) return;
    let cancelled = false;
    let card = null;
    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        stripeRef.current = Stripe(publishableKeyRef.current);
        const elements = stripeRef.current.elements();
        card = elements.create("card", { hidePostalCode: true });
        card.mount("#cc-card-element");
        cardRef.current = card;
      } catch (e) {
        toast.error("Failed to load the card form. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
      if (card) {
        try { card.destroy(); } catch (e) { /* already gone */ }
      }
      if (cardRef.current === card) cardRef.current = null;
    };
  }, [cardReady]);

  // ---- Pay + initiate call ------------------------------------------------
  const handlePay = async () => {
    if (!stripeRef.current || !cardRef.current || !clientSecretRef.current) {
      toast.error("Payment form is not ready yet.");
      return;
    }
    setPayLoading(true);
    try {
      const result = await stripeRef.current.confirmCardPayment(clientSecretRef.current, {
        payment_method: { card: cardRef.current, billing_details: { name: fullName, email } },
      });
      if (result.error) {
        toast.error(result.error.message || "Payment failed.");
        return;
      }
      // With manual capture the card is authorized (held) but not charged, so
      // the intent settles in "requires_capture". "succeeded" is only returned
      // if the backend is using automatic capture. Accept both as success.
      const intentStatus = result.paymentIntent?.status;
      if (intentStatus !== "requires_capture" && intentStatus !== "succeeded") {
        toast.error("Payment could not be authorized.");
        return;
      }

      const callData = new FormData();
      callData.append("call_package_id", selectedPackage.id);
      callData.append("category_id", categoryId);
      callData.append("jurisdiction_id", jurisdictionId);
      callData.append("case_description", caseDescription);
      callData.append("amount_paid", selectedPackage.price);
      callData.append("currency", selectedPackage.currency || "USD");
      callData.append("transaction_id", result.paymentIntent.id);
      callData.append("payment_status", intentStatus === "requires_capture" ? "authorized" : "paid");

      const callRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/storeCallHistory`,
        callData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (callRes?.data?.status) {
        const callId = callRes.data.data?.call_id;
        setShowCheckout(false);
        resetCheckout();
        // Show the "finding a lawyer" screen right away so there's no blank
        // gap while initiateConsultation (2-3s) is in flight. It starts in the
        // "finding…" loading state and flips to "lawyers found" once the request
        // returns with the matched lawyers.
        setLawyersFound(false);
        setCallPhase("searching");
        phaseRef.current = "searching";
        await startSearching(callId);
      } else {
        toast.error(callRes?.data?.message || "Payment succeeded but booking failed. Please contact support.");
      }
    } catch (e) {
      toast.error("An error occurred while processing your payment.");
    } finally {
      setPayLoading(false);
    }
  };

  // ---- Broadcast the request to matched lawyers and wait for an accept ------
  const SEARCH_TIMEOUT_MS = 60000;

  const startSearching = async (callId) => {
    try {
      const reqData = new FormData();
      reqData.append("call_id", callId);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/initiateConsultation`,
        reqData,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not start the search. Please contact support.");
        resetCall();
        return;
      }
      const d = res.data.data;
      const lawyers = d.lawyers || [];
      setMatchedLawyers(lawyers);
      matchedRef.current = lawyers;
      setAvailableCount(d.available ?? lawyers.length);
      setLawyersFound(true); // flip the search screen from "finding…" to "found"

      const call = {
        callId: d.callId,
        channel: d.channel,
        creds: { appId: d.agoraAppId, channel: d.channel, token: d.token, uid: d.uid },
        lawyer: null,
        user: d.user,
        // Paid plan length in minutes — drives the in-call countdown/auto-end.
        durationMinutes: Number(selectedPackage?.duration_minutes) || 0,
        // The OTP-verified token so the in-call chat can call the persisted
        // chat APIs (getChatMessages / sendMessage) on the user's behalf.
        authToken: getAuthToken(),
        // Snapshot of what the user entered on the form, shown during the call.
        // Captured here so it survives the checkout/form reset after payment.
        caseDetails: {
          category: categoriesList.find((c) => String(c.id) === String(categoryId))?.name || "",
          jurisdiction: jurisdictionsList.find((j) => String(j.id) === String(jurisdictionId))?.name || "",
          description: caseDescription,
          plan: selectedPackage
            ? `${selectedPackage.duration_minutes} Minutes${selectedPackage.title ? ` · ${selectedPackage.title}` : ""}`
            : "",
        },
      };
      callRef.current = call;
      setActiveCall(call);
      setCallPhase("searching");

      // Register this socket as the caller and broadcast to matched lawyers.
      SocketService.emit("user-connected", { id: d.user?.id, name: d.user?.name });
      SocketService.emit("consultation-initiate", {
        callId: d.callId,
        channel: d.channel,
        lawyer_ids: lawyers.map((l) => l.id),
        user: d.user,
      });

      // No-lawyer-accepted timeout — release the held payment and stop.
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        if (phaseRef.current === "searching") {
          toast.info("No lawyer is available right now. Your card was not charged.");
          cancelSearch();
        }
      }, SEARCH_TIMEOUT_MS);
    } catch (e) {
      toast.error("An error occurred while finding a lawyer.");
      resetCall();
    }
  };

  // Cancel while still searching: release the authorization (no charge).
  const cancelSearch = () => {
    const c = callRef.current;
    clearTimeout(searchTimer.current);
    if (c) {
      SocketService.emit("consultation-cancel", { callId: c.callId });
      axios.post(
        `${process.env.REACT_APP_API_URL}/endCall`,
        (() => { const f = new FormData(); f.append("callId", c.callId); return f; })(),
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      ).catch(() => {}).finally(() => localStorage.removeItem(AUTH_TOKEN_KEY));
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
    resetCall();
  };

  // Hang up an active call: backend captures the held payment.
  const endActiveCall = () => {
    const c = callRef.current;
    if (c) {
      SocketService.emit("call-end", { callId: c.callId });
      axios.post(
        `${process.env.REACT_APP_API_URL}/endCall`,
        (() => { const f = new FormData(); f.append("callId", c.callId); return f; })(),
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      ).catch(() => {}).finally(() => localStorage.removeItem(AUTH_TOKEN_KEY));
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
    resetCall();
  };

  const resetCall = () => {
    clearTimeout(searchTimer.current);
    callRef.current = null;
    matchedRef.current = [];
    setActiveCall(null);
    setCallPhase("idle");
    setMatchedLawyers([]);
    setLawyersFound(false);
  };

  // Socket listeners for the whole call lifecycle.
  useEffect(() => {
    const unsubs = [
      SocketService.subscribe("call-accepted", (payload) => {
        if (phaseRef.current !== "searching") return;
        clearTimeout(searchTimer.current);
        const c = callRef.current;
        if (!c) return;
        // The lawyer side's accept payload only carries { id, name }. The
        // matched-lawyers list has the full profile (picture, rating, categories),
        // so merge with the list taking priority and the socket payload as a
        // fallback (e.g. direct calls that never populated the list).
        const fromList = (matchedRef.current || []).find(
          (l) => String(l.id) === String(payload?.lawyer_id)
        );
        const lawyer =
          fromList || payload?.lawyer
            ? { ...(payload?.lawyer || {}), ...(fromList || {}) }
            : null;
        const next = { ...c, lawyer };
        callRef.current = next;
        setActiveCall(next);
        setCallPhase("connected");
      }),
      SocketService.subscribe("call-ended", () => {
        if (phaseRef.current === "connected") {
          toast.info("The call has ended.");
          localStorage.removeItem(AUTH_TOKEN_KEY);
          resetCall();
        }
      }),
    ];
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tear down all checkout/payment state so the next open starts clean. Setting
  // cardReady false triggers the mount effect's cleanup, which destroys the card
  // element; we still null the refs here in case the card never mounted.
  const resetCheckout = () => {
    setCardReady(false);
    setOtp("");
    setOtpVerified(false);
    setAuthToken(null);
    if (cardRef.current) {
      try { cardRef.current.destroy(); } catch (e) { /* already gone */ }
    }
    stripeRef.current = null;
    cardRef.current = null;
    clientSecretRef.current = null;
    publishableKeyRef.current = null;
  };

  const closeCheckout = () => {
    setShowCheckout(false);
    resetCheckout();
    // Abandoning the modal before starting a call — drop the persisted token.
    localStorage.removeItem(AUTH_TOKEN_KEY);
  };

  const fmtPrice = (p) => `${p.currency || "USD"} ${Number(p.price).toFixed(0)}`;

  return (
    <div className="cc-page font-inter">
      <Seo
        title="Consultation Call | Talk to a Lawyer on Video - Legal Platform"
        description="Connect with a verified lawyer on a video call within minutes. Choose your category, jurisdiction and call duration to get instant legal assistance."
        canonicalPath={process.env.REACT_APP_BASE_PATH + "/consultation-call"}
        image="/favicon.png"
      />
      <Header />

      <main className="cc-main container py-5">
        <div className="cc-hero-grid">
          {/* ----------------------- LEFT: FORM ----------------------- */}
          <div className="cc-form-card">
            <div className="text-center">
              <div className="cc-badge-pill">
                <i className="bi bi-lightning-charge-fill" /> INSTANT LEGAL ASSISTANCE
              </div>
              <h1 className="cc-title">Connect with a lawyer in Minutes</h1>
              <p className="cc-subtitle">
                Fill in the details below and we will connect you with an available lawyer right away.
              </p>
            </div>

            <div className="cc-field">
              <label className="cc-label">Legal Category</label>
              <select className="form-select cc-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Select legal category</option>
                {categoriesList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">Jurisdiction</label>
              <select className="form-select cc-select" value={jurisdictionId} onChange={(e) => setJurisdictionId(e.target.value)}>
                <option value="">Select jurisdiction</option>
                {jurisdictionsList.map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">Explain Your Issue</label>
              <textarea
                className="form-control cc-textarea"
                placeholder="Please describe your legal in detail..."
                maxLength={500}
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
              />
            </div>

            <div className="cc-field">
              <label className="cc-label">Select Call Duration</label>
              <div className="cc-duration-grid">
                {packages.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className={`cc-duration-card ${selectedPackage?.id === p.id ? "selected" : ""}`}
                    onClick={() => setSelectedPackage(p)}
                  >
                    <i className="bi bi-clock cc-duration-icon" />
                    <span className="cc-duration-title">{p.duration_minutes} Minutes</span>
                    <span className="cc-duration-price">{fmtPrice(p)}</span>
                    {p.is_popular && <span className="cc-duration-pop">Popular</span>}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" className="cc-cta-btn" onClick={handleStartConnecting}>
              <i className="bi bi-camera-video-fill me-2" />
              Start Connecting with Available Lawyer
            </button>
            <div className="cc-secure-note">
              <i className="bi bi-lock-fill" /> Your information is secure and encrypted
            </div>
          </div>

          {/* ----------------------- RIGHT: HERO ----------------------- */}
          <div className="cc-hero-visual">
            <img src={lawyerImg} alt="Verified lawyer ready to help" className="cc-hero-img" />

            <div className="cc-float cc-float-online">
              <span className="cc-online-dot" />
              <div>
                <div className="cc-float-strong">42 <span>Lawyers Online</span></div>
                <div className="cc-float-sub">Ready to help you</div>
              </div>
            </div>

            <div className="cc-float cc-float-stats">
              <div className="cc-stat">
                <i className="bi bi-clock-history" />
                <div>
                  <div className="cc-stat-label">Average Connection Time</div>
                  <div className="cc-stat-strong">32 Seconds</div>
                </div>
              </div>
              <div className="cc-stat">
                <i className="bi bi-shield-check" />
                <div>
                  <div className="cc-stat-label">100% Confidential</div>
                  <div className="cc-stat-sub">Your privacy is our priority</div>
                </div>
              </div>
              <div className="cc-stat">
                <i className="bi bi-camera-video" />
                <div>
                  <div className="cc-stat-label">Secure Video Calls</div>
                  <div className="cc-stat-sub">End-to-end encrypted</div>
                </div>
              </div>
            </div>

            <div className="cc-float cc-float-sos">
              <div className="cc-sos-head"><i className="bi bi-exclamation-octagon-fill" /> SOS</div>
              <div className="cc-sos-title">Emergency Call</div>
              <div className="cc-sos-text">Connect instantly with a lawyer in emergency.</div>
              <button type="button" className="cc-sos-btn" onClick={handleStartConnecting}>
                <i className="bi bi-telephone-fill me-1" /> Call Now
              </button>
            </div>
          </div>
        </div>

        {/* ----------------------- HOW IT WORKS ----------------------- */}
        <div className="cc-how">
          <div className="cc-how-title">HOW IT WORKS</div>
          <div className="cc-how-grid">
            {HOW_IT_WORKS.map((s, i) => (
              <React.Fragment key={s.title}>
                <div className="cc-how-item">
                  <div className="cc-how-icon">
                    <i className={`bi ${s.icon}`} />
                    <span className="cc-how-num">{i + 1}</span>
                  </div>
                  <div className="cc-how-name">{s.title}</div>
                  <div className="cc-how-text">{s.text}</div>
                </div>
                {i < HOW_IT_WORKS.length - 1 && <div className="cc-how-arrow"><i className="bi bi-arrow-right" /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </main>

      {/* ----------------------- CHECKOUT MODAL ----------------------- */}
      {showCheckout && (
        <div className="cc-modal-overlay" onClick={closeCheckout}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cc-modal-close" onClick={closeCheckout} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>

            <>
                <h3 className="cc-modal-title">Confirm &amp; Pay</h3>
                <div className="cc-summary">
                  <div className="cc-summary-row">
                    <span>Consultation</span>
                    <span>{selectedPackage?.title} ({selectedPackage?.duration_minutes} min)</span>
                  </div>
                  <div className="cc-summary-row cc-summary-total">
                    <span>Total</span>
                    <span>{selectedPackage ? fmtPrice(selectedPackage) : "-"}</span>
                  </div>
                </div>

                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <input type="text" className="form-control cc-input" placeholder="Full Name"
                      value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={otpVerified} />
                  </div>
                  <div className="col-md-6">
                    <input type="email" className="form-control cc-input" placeholder="Email Address"
                      value={email} onChange={(e) => setEmail(e.target.value)} disabled={otpVerified} />
                  </div>
                </div>

                {!otpVerified ? (
                  <div className="postq-otp-block mt-2">
                    <button type="button" className="postq-otp-btn w-100" onClick={handleSendOtp} disabled={otpLoading}>
                      {otpLoading ? "Sending..." : "Send OTP"}
                    </button>
                    <input type="text" className="postq-otp-input" placeholder="Enter OTP" value={otp}
                      onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) setOtp(e.target.value); }} maxLength="4" />
                  </div>
                ) : (
                  <div className="cc-verified mt-2"><i className="bi bi-patch-check-fill" /> Email verified</div>
                )}

                <div className="cc-modal-sub mt-3">Payment</div>
                {!cardReady ? (
                  <button type="button" className="cc-cta-btn" onClick={initCard} disabled={!otpVerified || payLoading}>
                    {payLoading ? "Preparing..." : `Enter Card Details — ${selectedPackage ? fmtPrice(selectedPackage) : ""}`}
                  </button>
                ) : (
                  <>
                    <div id="cc-card-element" className="cc-card-box" />
                    <button type="button" className="cc-cta-btn mt-3" onClick={handlePay} disabled={payLoading}>
                      <i className="bi bi-camera-video-fill me-2" />
                      {payLoading ? "Processing..." : `Authorize & Initiate Call — ${selectedPackage ? fmtPrice(selectedPackage) : ""}`}
                    </button>
                    <div className="cc-secure-note">
                      <i className="bi bi-shield-lock-fill" /> Your card is only held now — charged after your call ends.
                    </div>
                  </>
                )}
                <div className="cc-secure-note"><i className="bi bi-lock-fill" /> Secured by Stripe</div>
              </>
          </div>
        </div>
      )}

      {/* Searching for a lawyer (after payment is authorized) */}
      {callPhase === "searching" && (
        <FindingLawyer
          lawyers={matchedLawyers}
          available={availableCount}
          found={lawyersFound}
          onCancel={cancelSearch}
        />
      )}

      {/* Live video call once a lawyer accepts */}
      {callPhase === "connected" && activeCall && (
        <CallScreen call={activeCall} onEnd={endActiveCall} />
      )}

      <Footer />
    </div>
  );
};

export default ConsultationCall;
